<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue'

  import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
  import CourseTile from '@/components/course/CourseTile.vue'
  import KetchUpButton from '@/components/common/KetchUpButton.vue'
  import Dropdown from '@/components/common/Dropdown.vue'

  import { useRoute } from 'vue-router/composables'
  import useEditor from '@/composables/useEditor'
  import useI18n from '@/composables/useI18n'

  import { CourseModule } from '@/store/modules/course'

  import eventBus from '@/main'
  import useCourse from '@/composables/useCourse'
  import type { DropdownItem } from '@/services/interfaces/Common'

  interface CourseData {
    courseId: string
    titleHeight: number
    state: string
  }

  const route = useRoute()
  const { inEditorMode } = useEditor(route)
  const { courseStateText } = useCourse()
  const { translateString } = useI18n()

  const courseData = ref<CourseData[]>([])
  const searchQuery = ref('')
  const selectedState = ref<string>('all')
  const selectedIddCourse = ref<string>('all')

  const loadingCourses = computed(() => allCourses.value === null)

  const allCourses = computed(() => CourseModule.courses)

  const filteredCourses = computed(() => {
    if (!allCourses.value) return []

    const filtered = allCourses.value.filter((course) => {
      const matchesSearch =
        searchQuery.value === '' || course.title.toLowerCase().includes(searchQuery.value.toLowerCase())

      const matchesState = selectedState.value === 'all' || course.state === selectedState.value

      const matchesIddCourse =
        selectedIddCourse.value === 'all' ||
        (selectedIddCourse.value === 'true' && course.isIddCourse) ||
        (selectedIddCourse.value === 'false' && !course.isIddCourse)

      return matchesSearch && matchesState && matchesIddCourse
    })

    return filtered.sort((a, b) => {
      const dateA = new Date(a.updatedAt).getTime()
      const dateB = new Date(b.updatedAt).getTime()
      return dateB - dateA
    })
  })

  const availableStates = computed(() => {
    if (!allCourses.value) return []

    const states = [...new Set(allCourses.value.map((course) => course.state))]
    return states.sort()
  })

  const stateDropdownItems = computed((): DropdownItem[] => {
    const items: DropdownItem[] = [
      {
        type: 'all',
        title: translateString('editor.allStates'),
        selectable: true,
      },
    ]

    availableStates.value.forEach((state) => {
      items.push({
        type: state,
        title: courseStateText.value(state),
        selectable: true,
      })
    })

    return items
  })

  const iddCourseDropdownItems = computed((): DropdownItem[] => [
    {
      type: 'all',
      title: translateString('editor.allCourses'),
      selectable: true,
    },
    {
      type: 'true',
      title: translateString('editor.iddCoursesOnly'),
      selectable: true,
    },
    {
      type: 'false',
      title: translateString('editor.nonIddCoursesOnly'),
      selectable: true,
    },
  ])

  const maxHeight = computed(() => {
    if (courseData.value.length === 0) return 0

    const maxTitleHeight = Math.max(...courseData.value.map((course) => course.titleHeight))

    return maxTitleHeight > 0 ? maxTitleHeight : 0
  })

  const onCourseData = (data: CourseData) => {
    const existingIndex = courseData.value.findIndex((course) => course.courseId === data.courseId)

    if (existingIndex >= 0) {
      courseData.value[existingIndex] = data
    } else {
      courseData.value.push(data)
    }
  }

  const clearFilters = () => {
    searchQuery.value = ''
    selectedState.value = 'all'
    selectedIddCourse.value = 'all'
  }

  const openCreateCourseModal = () => {
    eventBus.$emit('show-modal', {
      modalContentComponent: 'CreateCourseModal',
      modalProps: {},
      closeModalOnOutsideClick: false,
      cssClass: 'create-course-modal',
      modalCloseCallback: (callback: () => void) => {
        if (typeof callback === 'function') callback()
      },
    })
  }

  onMounted(() => {
    CourseModule.setCourse(null)
    CourseModule.setCourseBundle(null)
    CourseModule.getCourses()
  })
</script>

<template lang="pug">
.editor-dashboard
  LoadingSpinner(v-if='loadingCourses || !inEditorMode')
  template(v-else)
    .dashboard-header
      h1.ketch-font-bold Editor Dashboard
      KetchUpButton.editor(@click.native='openCreateCourseModal')
        h5 {{ $t('editor.createCourse.createCourseTitle') }}

    .filters-panel
      .filters-wrapper
        .search-filter
          input.search-input(
            v-model='searchQuery',
            type='text',
            :placeholder='String($t("editor.searchCoursesByTitle"))'
          )

        .state-filter
          Dropdown(
            :items='stateDropdownItems',
            v-model='selectedState',
            :default-dropdown-behavior='true',
            :dropdown-placeholder='String($t("editor.allStates"))'
          )

        .idd-course-filter
          Dropdown(
            :items='iddCourseDropdownItems',
            v-model='selectedIddCourse',
            :default-dropdown-behavior='true',
            :dropdown-placeholder='String($t("editor.allCourses"))'
          )

        .clear-filters
          KetchUpButton.tertiary.ketch-w-auto(
            @click.native='clearFilters',
            :disabled='searchQuery === "" && selectedState === "all" && selectedIddCourse === "all"'
          )
            h5 {{ $t('editor.clearFilters') }}

    .courses-info
      h5 {{ $t('editor.showingCourses', { count: filteredCourses.length, total: allCourses?.length || 0 }) }}

    .courses-wrapper
      CourseTile(
        v-for='course in filteredCourses',
        :course='course',
        :key='course.id',
        @course-data='onCourseData',
        :max-height='maxHeight'
      )

    .no-courses(v-if='filteredCourses.length === 0 && !loadingCourses')
      h5 {{ $t('editor.noCoursesFoundMatchingFilters') }}
      KetchUpButton.tertiary(@click.native='clearFilters')
        h5 {{ $t('editor.clearFilters') }}
</template>

<style lang="postcss" scoped>
  .editor-dashboard {
    @apply ketch-flex ketch-flex-col ketch-gap-c20;

    .dashboard-header {
      @apply ketch-flex ketch-items-center ketch-justify-between;

      h1 {
        @apply ketch-m-0;
      }
    }

    .filters-panel {
      @apply ketch-bg-content-background-color ketch-rounded-normal ketch-shadow-content-shadow ketch-p-c10;

      .filters-wrapper {
        @apply ketch-flex ketch-flex-wrap ketch-gap-c20 ketch-items-center;

        .search-filter {
          @apply ketch-flex-1 ketch-min-w-[200px];

          .search-input {
            @apply ketch-w-full ketch-px-c10 ketch-py-c10 ketch-border ketch-border-border-color ketch-rounded-normal ketch-h-c40;
            @apply focus:ketch-outline-none focus:ketch-ring-2 focus:ketch-ring-editor-primary-color focus:ketch-border-transparent;
          }
        }

        .state-filter {
          @apply ketch-min-w-[160px];
        }

        .idd-course-filter {
          @apply ketch-min-w-[180px];
        }

        .clear-filters {
          @apply ketch-flex-shrink-0;
        }
      }
    }

    .courses-info {
      @apply ketch-text-sm ketch-text-gray-600 ketch-mb-2;
    }

    .courses-wrapper {
      @apply ketch-grid ketch-grid-cols-1 sm:ketch-grid-cols-2 lg:ketch-grid-cols-3 xl:ketch-grid-cols-4 ketch-gap-c20;
      @apply ketch-items-start;
    }

    .no-courses {
      @apply ketch-flex ketch-items-center ketch-justify-between ketch-p-c20;
      @apply ketch-border ketch-border-border-color ketch-rounded-normal ketch-shadow-content-shadow;
    }
  }
</style>
